// Handles solanoessence:// deep links fired by the iOS home-screen widgets.
// Mounted inside <BrowserRouter>. No-op on web.
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { refreshWidgetFromServer } from "@/lib/widgetSync";
import { drainPendingShare, onShareReceived, importRouteFor, importPhotosRoute, stashShareImages } from "@/lib/shareIntent";

const ROUTES = {
  water: "/app?water=1",
  meal: "/app/pasto",
  photo: "/app/pasto?mode=photo",
  diary: "/app/diario",
  activity: "/app/allenamenti",
  dashboard: "/app",
};

export default function DeepLinkListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("appUrlOpen", ({ url }) => {
          const u = String(url || "");
          // Universal Links: https://app.solano-essence.com/api/r/<id> | /api/share/{recipe,meal}/<id> | /app/...
          if (/^https?:\/\//i.test(u)) {
            try {
              const { pathname, search } = new URL(u);
              let m;
              if ((m = pathname.match(/^\/api\/(?:r|share\/recipe)\/([A-Za-z0-9_-]+)/))) { navigate(`/app/ricette?share=${m[1]}`); return; }
              if ((m = pathname.match(/^\/api\/share\/meal\/([A-Za-z0-9_-]+)/))) { navigate(`/app/pasto?share=${m[1]}`); return; }
              if (pathname.startsWith("/app")) { navigate(`${pathname}${search}`); return; }
            } catch { /* ignore malformed */ }
            return;
          }
          // Share links: solanoessence://open?path=/app/ricette?share=XYZ
          const mo = u.match(/^solanoessence:\/\/open\?path=(.+)$/i);
          if (mo) {
            try { navigate(decodeURIComponent(mo[1])); } catch { navigate(mo[1]); }
            return;
          }
          // Share-sheet import (iOS Share Extension): solanoessence://import?url=<encoded>
          // or solanoessence://import?photos=N (images wait in the App Group → getPending)
          if (/^solanoessence:\/\/import\?photos=/i.test(u)) {
            drainPendingShare().then((d) => {
              if (d?.images?.length) { stashShareImages(d.images); navigate(importPhotosRoute); }
            });
            return;
          }
          const mi = u.match(/^solanoessence:\/\/import\?url=(.+)$/i);
          if (mi) {
            let shared = mi[1];
            try { shared = decodeURIComponent(shared); } catch { /* keep raw */ }
            if (/^https?:\/\//i.test(shared)) navigate(importRouteFor(shared));
            return;
          }
          const m = u.match(/^solanoessence:\/\/([a-z-]+)/i);
          const to = m && ROUTES[m[1].toLowerCase()];
          // Unique ts param so landing on /app?water=… re-triggers the
          // highlight effect even when the user is already on the dashboard.
          if (to) navigate(to.includes("?") ? `${to}&ts=${Date.now()}` : to);
        });
      } catch { /* plugin missing on old binaries */ }
    })();
    return () => { handle?.remove(); };
  }, [navigate]);

  // Share-sheet imports (Android ACTION_SEND / iOS App-Group handoff):
  // drain anything received while closed, then listen while running.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let off = () => {};
    const go = (d) => {
      if (!d) return;
      if (d.images?.length) { stashShareImages(d.images); navigate(importPhotosRoute); return; }
      if (d.url && /^https?:\/\//i.test(d.url)) navigate(importRouteFor(d.url));
    };
    drainPendingShare().then(go);
    onShareReceived(go).then((fn) => { off = fn; });
    const onResume = (st) => { if (st?.isActive) drainPendingShare().then(go); };
    let handle;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("appStateChange", onResume);
      } catch {}
    })();
    return () => { off(); handle?.remove(); };
  }, [navigate]);

  // Keep the home-screen widget fresh: push server values whenever the app
  // is sent to background (user returning to the home screen) or resumed.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("appStateChange", () => {
          refreshWidgetFromServer();
        });
      } catch {}
    })();
    refreshWidgetFromServer();
    return () => { handle?.remove(); };
  }, []);
  return null;
}
